import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faKey,
    faPlus,
    faCopy,
    faPencil,
    faBan,
    faTrash,
    faCheck,
    faTimes,
    faCircleInfo,
    faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { Config } from 'config/envConfig';
import { NoDataView } from '../../noDataView';
import axios from '../../../utils/axiosConfig';
import { getUrl } from '../../pdfDisplay/utils';
import {
    alert,
    AlertType,
    Button,
    ConfirmModal,
    CustomTable,
    Input,
    Modal,
    NoInteractionView,
} from '@quyva/shared-ui';

const API_BASE = () =>
    getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/api/v1/api-keys`);

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function KeyCreatedModal({ apiKey, keyName, onClose }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(apiKey).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Modal
            title={`Key Created: ${keyName}`}
            titleIcon={faKey}
            iconColor='sky'
            onClose={onClose}
        >
            <div className='w-[90vw] md:w-[500px] p-2'>
                <div className='mb-4 p-3 bg-backgrounds-settings100 border border-backgrounds-settings500 rounded-xl flex items-start gap-2'>
                    <FontAwesomeIcon icon={faCircleInfo} className='text-icons-warning mt-0.5 flex-shrink-0' />
                    <p className='text-sm text-texts-secondary300 font-medium'>
                        This is the only time your API key will be shown. Copy and store it securely — it cannot be retrieved again.
                    </p>
                </div>
                <div className='mb-1 text-sm font-semibold text-texts-secondary300'>Your API Key</div>
                <div className='flex items-center gap-2 mb-5'>
                    <div className='flex-1 font-mono text-sm bg-backgrounds-primary100 border border-backgrounds-primary200 rounded-xl px-3 py-2 overflow-x-auto whitespace-nowrap text-texts-secondary300 select-all'>
                        {apiKey}
                    </div>
                    <Button
                        icon={copied ? faCheck : faCopy}
                        text={copied ? 'Copied!' : 'Copy'}
                        type={copied ? 'success' : 'primary'}
                        onClick={handleCopy}
                        hoverText='Copy to clipboard'
                    />
                </div>
                <div className='flex justify-end'>
                    <Button type='warning' text='Close' onClick={onClose} />
                </div>
            </div>
        </Modal>
    );
}

function CreateKeyModal({ onClose, onCreate }) {
    const [keyName, setKeyName] = useState('');
    const [description, setDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = keyName.trim();
        if (!trimmed) { setError('Key name is required'); return; }
        if (trimmed.length > 100) { setError('Key name must be 100 characters or less'); return; }
        if (description.length > 500) { setError('Description must be 500 characters or less'); return; }
        setError('');
        setIsCreating(true);
        try {
            await onCreate(trimmed, description.trim() || null);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Modal
            title='Create New API Key'
            titleIcon={faPlus}
            iconColor='sky'
            onClose={onClose}
        >
            <div className='w-[90vw] md:w-[450px] p-2'>
                <form onSubmit={handleSubmit}>
                    <div className='mb-4'>
                        <label className='block text-sm font-semibold text-texts-secondary300 mb-2'>
                            Key Name <span className='text-texts-red400'>*</span>
                        </label>
                        <Input
                            type='text'
                            value={keyName}
                            onChange={(e) => { setKeyName(e.target.value); setError(''); }}
                            placeholder='e.g. Production Integration'
                            maxLength={100}
                            autoFocus
                        />
                        <div className='flex justify-between mt-1'>
                            {error ? <span className='text-texts-red400 text-xs'>{error}</span> : <span />}
                            <span className='text-xs text-texts-secondary300 opacity-60'>{keyName.length}/100</span>
                        </div>
                    </div>
                    <div className='mb-5'>
                        <label className='block text-sm font-semibold text-texts-secondary300 mb-2'>
                            Description <span className='text-xs font-normal opacity-60'>(optional)</span>
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder='Describe what this key is used for'
                            maxLength={500}
                            rows={3}
                            className='w-full px-3 py-2 rounded-xl border hover:outline-none focus:outline-backgrounds-primary200 hover:border-backgrounds-primary300 text-texts-secondary300 text-sm resize-none'
                        />
                        <div className='flex justify-end mt-0.5'>
                            <span className='text-xs text-texts-secondary300 opacity-60'>{description.length}/500</span>
                        </div>
                    </div>
                    <div className='flex justify-end gap-3'>
                        <Button type='warning' text='Cancel' onClick={onClose} disabled={isCreating} />
                        <Button
                            type='success'
                            text={isCreating ? 'Creating...' : 'Create Key'}
                            disabled={isCreating || !keyName.trim()}
                            onClick={handleSubmit}
                        />
                    </div>
                </form>
            </div>
        </Modal>
    );
}

function RenameInput({ currentName, onSave, onCancel }) {
    const [value, setValue] = useState(currentName);

    return (
        <div className='flex items-center gap-1'>
            <Input
                type='text'
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave(value.trim());
                    if (e.key === 'Escape') onCancel();
                }}
                maxLength={100}
                autoFocus
            />
            <Button icon={faCheck} type='success' hoverText='Save' onClick={() => onSave(value.trim())} disabled={!value.trim()} />
            <Button icon={faTimes} type='warning' hoverText='Cancel' onClick={onCancel} />
        </div>
    );
}

const PAGE_SIZE = 20;

export const ApiKeyManagementContent = ({ orgId = null }) => {
    const [keys, setKeys] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchKeys = useCallback(async (pageNum) => {
        try {
            setIsLoading(true);
            const params = { page: pageNum, limit: PAGE_SIZE };
            if (orgId) params.org_id = orgId;
            const res = await axios.get(API_BASE(), { params, withCredentials: true });
            const data = res.data?.data || {};
            setKeys(data.keys || []);
            setTotal(data.total || 0);
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to load API keys');
        } finally {
            setIsLoading(false);
        }
    }, [orgId]);

    useEffect(() => { fetchKeys(page); }, [fetchKeys, page]);

    const handleCreate = async (keyName, description) => {
        try {
            const res = await axios.post(API_BASE(), { name: keyName, description }, { withCredentials: true });
            setNewlyCreatedKey(res.data.data);
            setShowCreateModal(false);
            setPage(1);
            await fetchKeys(1);
        } catch (err) {
            alert(AlertType.ERROR, err.response?.data?.error || 'Failed to create API key');
        }
    };

    const handleRename = async (keyId, newName) => {
        if (!newName) { setRenamingId(null); return; }
        try {
            await axios.patch(`${API_BASE()}/${keyId}`, { name: newName }, { withCredentials: true });
            setRenamingId(null);
            await fetchKeys(page);
        } catch (err) {
            alert(AlertType.ERROR, err.response?.data?.error || 'Failed to rename API key');
        }
    };

    const handleRevoke = async () => {
        if (!revokeTarget) return;
        try {
            await axios.post(`${API_BASE()}/${revokeTarget.id}/revoke`, {}, { withCredentials: true });
            setRevokeTarget(null);
            await fetchKeys(page);
        } catch (err) {
            alert(AlertType.ERROR, err.response?.data?.error || 'Failed to revoke API key');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await axios.delete(`${API_BASE()}/${deleteTarget.id}`, { withCredentials: true });
            setDeleteTarget(null);
            const newPage = keys.length === 1 && page > 1 ? page - 1 : page;
            setPage(newPage);
            await fetchKeys(newPage);
        } catch (err) {
            alert(AlertType.ERROR, err.response?.data?.error || 'Failed to delete API key');
        }
    };

    const filteredKeys = keys.filter((k) => {
        const matchesSearch = !searchQuery ||
            k.key_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (k.description && k.description.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && !k.is_revoked) ||
            (statusFilter === 'revoked' && k.is_revoked);
        return matchesSearch && matchesStatus;
    });

    const activeCount = keys.filter((k) => !k.is_revoked).length;

    const columns = [
        {
            title: 'Name',
            key: 'key_name',
            render: (_, key) => renamingId === key.id ? (
                <RenameInput
                    currentName={key.key_name}
                    onSave={(name) => handleRename(key.id, name)}
                    onCancel={() => setRenamingId(null)}
                />
            ) : (
                <div>
                    <div className='font-medium text-texts-secondary300'>{key.key_name}</div>
                    {key.description && (
                        <div className='text-xs text-texts-secondary300 opacity-60 mt-0.5 max-w-[200px] truncate' title={key.description}>
                            {key.description}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Key Prefix',
            dataIndex: 'key_prefix',
            key: 'key_prefix',
            render: (val) => (
                <code className='font-mono text-xs bg-backgrounds-primary100 border border-backgrounds-primary200 px-2 py-0.5 rounded'>
                    {val}...
                </code>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (val) => <span className='text-xs text-texts-secondary300 whitespace-nowrap'>{formatDate(val)}</span>,
        },
        {
            title: 'Last Used',
            dataIndex: 'last_used_at',
            key: 'last_used_at',
            render: (val) => <span className='text-xs text-texts-secondary300 whitespace-nowrap'>{formatDate(val)}</span>,
        },
        {
            title: 'Requests',
            dataIndex: 'usage_count',
            key: 'usage_count',
            render: (val) => <span className='text-xs text-texts-secondary300'>{(val || 0).toLocaleString()}</span>,
        },
        {
            title: 'Status',
            key: 'is_revoked',
            render: (_, key) => key.is_revoked ? (
                <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200'>
                    <FontAwesomeIcon icon={faBan} className='text-xs' /> Revoked
                </span>
            ) : (
                <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200'>
                    <FontAwesomeIcon icon={faCheck} className='text-xs' /> Active
                </span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, key) => (
                <div className='flex items-center gap-1'>
                    {!key.is_revoked && (
                        <>
                            <Button icon={faPencil} type='success' hoverText='Rename key' onClick={() => setRenamingId(key.id)} />
                            <Button icon={faBan} type='danger' hoverText='Revoke key' onClick={() => setRevokeTarget(key)} />
                        </>
                    )}
                    {key.is_revoked && (
                        <Button icon={faTrash} type='danger' hoverText='Delete permanently' onClick={() => setDeleteTarget(key)} />
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className='w-[90vw] md:w-[800px] p-4'>
            <div className='flex items-center justify-between gap-2 mb-3'>
                <span className='text-sm text-texts-secondary300 font-medium'>
                    <span className='font-semibold'>{activeCount}/10</span> active keys
                    {orgId && <span className='ml-2 text-xs opacity-60'>(org: {orgId})</span>}
                </span>
                {!orgId && (
                    <Button
                        type='success'
                        icon={faPlus}
                        text='Create New Key'
                        onClick={() => setShowCreateModal(true)}
                        hoverText='Create a new API key'
                        disabled={activeCount >= 10}
                    />
                )}
            </div>

            <div className='flex flex-col sm:flex-row gap-3 mb-4'>
                <div className='relative flex-1'>
                    <FontAwesomeIcon icon={faSearch} className='absolute left-3 top-1/2 -translate-y-1/2 text-texts-secondary300 opacity-40 text-xs pointer-events-none' />
                    <Input
                        type='text'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder='Search by name or description...'
                        className='pl-8'
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className='px-3 py-2 rounded-xl border hover:outline-none hover:border-backgrounds-primary300 text-texts-secondary300 text-sm'
                >
                    <option value='all'>All Status</option>
                    <option value='active'>Active</option>
                    <option value='revoked'>Revoked</option>
                </select>
            </div>

            <div className='h-[55vh] overflow-y-auto'>
                {isLoading ? (
                    <NoInteractionView viewText='Loading API keys...' />
                ) : filteredKeys.length === 0 ? (
                    <NoDataView
                        icon={faKey}
                        iconColor='icons-primary100'
                        text={total === 0 ? 'No API keys yet. Click Create New Key to get started.' : 'No keys match your filter.'}
                    />
                ) : (
                    <CustomTable
                        className='[&_.ant-table-thead>tr>th]:!bg-backgrounds-primary100'
                        dataSource={filteredKeys.map((k) => ({ ...k, key: k.id }))}
                        columns={columns}
                        rowKey='id'
                        pagination={{
                            current: page,
                            pageSize: PAGE_SIZE,
                            total,
                            showSizeChanger: false,
                            onChange: (p) => setPage(p),
                        }}
                    />
                )}
            </div>

            {showCreateModal && <CreateKeyModal onClose={() => setShowCreateModal(false)} onCreate={handleCreate} />}
            {newlyCreatedKey && (
                <KeyCreatedModal
                    apiKey={newlyCreatedKey.api_key}
                    keyName={newlyCreatedKey.name}
                    onClose={() => setNewlyCreatedKey(null)}
                />
            )}
            {revokeTarget && (
                <ConfirmModal
                    modalText={`Revoke "${revokeTarget.key_name}"? This key will immediately stop working and cannot be reactivated.`}
                    onConfirm={handleRevoke}
                    onCancel={() => setRevokeTarget(null)}
                    confirmText='Revoke'
                    cancelText='Cancel'
                />
            )}
            {deleteTarget && (
                <ConfirmModal
                    modalText={`Permanently delete "${deleteTarget.key_name}"? This action cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    confirmText='Delete'
                    cancelText='Cancel'
                />
            )}
        </div>
    );
};
